import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Brain, BookOpen, FileText, Loader2, CheckCircle, Sparkles,
  Calendar, ClipboardList, Gamepad2, Map, Presentation, UserCog,
  FileQuestion, ListChecks, Video, Image, FlipHorizontal, Grid3X3,
  PenLine, ListOrdered, RefreshCw, BookText, MessageCircle, Copy,
  Download, ChevronLeft, Lightbulb, Target, GraduationCap, Puzzle,
  Wand2, Send, X, ArrowRight
} from 'lucide-react';
import api from '../lib/api';

// Categorias de ferramentas
const CATEGORIAS = [
  { id: 'planejamento', nome: 'Planejamento', icon: Calendar, cor: 'bg-blue-500' },
  { id: 'atividades', nome: 'Atividades', icon: ClipboardList, cor: 'bg-green-500' },
  { id: 'avaliacoes', nome: 'Avaliações', icon: Target, cor: 'bg-purple-500' },
  { id: 'recursos', nome: 'Recursos', icon: Lightbulb, cor: 'bg-orange-500' },
  { id: 'comunicacao', nome: 'Comunicação', icon: MessageCircle, cor: 'bg-pink-500' },
];

// Ferramentas disponíveis
const FERRAMENTAS = [
  // Planejamento
  { id: 'plano_aula', nome: 'Plano de Aula', categoria: 'planejamento', icon: Calendar, descricao: 'Gere planos de aula completos alinhados à BNCC', prompt: 'plano_aula' },
  { id: 'sequencia_didatica', nome: 'Sequência Didática', categoria: 'planejamento', icon: ListOrdered, descricao: 'Crie sequências didáticas estruturadas', prompt: 'sequencia_didatica' },
  { id: 'projeto_pedagogico', nome: 'Projeto Pedagógico', categoria: 'planejamento', icon: BookText, descricao: 'Elabore projetos pedagógicos interdisciplinares', prompt: 'projeto_pedagogico' },
  { id: 'pei', nome: 'PEI - Plano Educacional', categoria: 'planejamento', icon: UserCog, descricao: 'Plano Educacional Individualizado para inclusão', prompt: 'pei' },
  
  // Atividades
  { id: 'lista_exercicios', nome: 'Lista de Exercícios', categoria: 'atividades', icon: ListChecks, descricao: 'Gere listas de exercícios personalizadas', prompt: 'lista_exercicios' },
  { id: 'jogos_pedagogicos', nome: 'Jogos Pedagógicos', categoria: 'atividades', icon: Gamepad2, descricao: 'Crie jogos educativos e dinâmicas', prompt: 'jogos_pedagogicos' },
  { id: 'palavra_cruzada', nome: 'Palavra Cruzada', categoria: 'atividades', icon: Grid3X3, descricao: 'Gere palavras cruzadas temáticas', prompt: 'palavra_cruzada' },
  { id: 'flashcards', nome: 'Flashcards', categoria: 'atividades', icon: FlipHorizontal, descricao: 'Crie flashcards para memorização', prompt: 'flashcards' },
  { id: 'tarefa_adaptada', nome: 'Tarefas Adaptadas', categoria: 'atividades', icon: UserCog, descricao: 'Adapte atividades para inclusão', prompt: 'tarefa_adaptada' },
  { id: 'caca_palavras', nome: 'Caça-Palavras', categoria: 'atividades', icon: Grid3X3, descricao: 'Gere caça-palavras temáticos', prompt: 'caca_palavras' },
  
  // Avaliações
  { id: 'prova', nome: 'Elaborar Prova', categoria: 'avaliacoes', icon: FileQuestion, descricao: 'Crie provas e avaliações completas', prompt: 'prova' },
  { id: 'simulado_saeb', nome: 'Simulado SAEB', categoria: 'avaliacoes', icon: Target, descricao: 'Gere questões no padrão SAEB', prompt: 'simulado_saeb' },
  { id: 'corretor_redacao', nome: 'Corretor de Redação', categoria: 'avaliacoes', icon: PenLine, descricao: 'Corrija redações com feedback detalhado', prompt: 'corretor_redacao' },
  { id: 'rubrica', nome: 'Criar Rubrica', categoria: 'avaliacoes', icon: ClipboardList, descricao: 'Elabore rubricas de avaliação', prompt: 'rubrica' },
  { id: 'relatorio_aluno', nome: 'Relatório Individual', categoria: 'avaliacoes', icon: FileText, descricao: 'Gere relatório individual do aluno', prompt: 'relatorio_aluno' },
  
  // Recursos
  { id: 'mapa_mental', nome: 'Mapa Mental', categoria: 'recursos', icon: Map, descricao: 'Gere mapas mentais de conteúdos', prompt: 'mapa_mental' },
  { id: 'resumo', nome: 'Resumo de Conteúdo', categoria: 'recursos', icon: FileText, descricao: 'Resuma textos e conteúdos complexos', prompt: 'resumo' },
  { id: 'reescritor', nome: 'Reescritor de Texto', categoria: 'recursos', icon: RefreshCw, descricao: 'Reescreva textos para diferentes níveis', prompt: 'reescritor' },
  { id: 'questoes_video', nome: 'Questões sobre Vídeo', categoria: 'recursos', icon: Video, descricao: 'Crie questões baseadas em vídeos', prompt: 'questoes_video' },
  { id: 'vocabulario', nome: 'Lista de Vocabulário', categoria: 'recursos', icon: BookOpen, descricao: 'Gere listas de vocabulário temáticas', prompt: 'vocabulario' },
  { id: 'proposta_redacao', nome: 'Proposta de Redação', categoria: 'recursos', icon: PenLine, descricao: 'Crie propostas de redação motivadoras', prompt: 'proposta_redacao' },
  { id: 'explicador', nome: 'Explicador de Conteúdo', categoria: 'recursos', icon: Lightbulb, descricao: 'Explique conceitos de forma simples', prompt: 'explicador' },
  
  // Comunicação
  { id: 'comunicado_pais', nome: 'Comunicado aos Pais', categoria: 'comunicacao', icon: MessageCircle, descricao: 'Crie comunicados para responsáveis', prompt: 'comunicado_pais' },
  { id: 'parecer_descritivo', nome: 'Parecer Descritivo', categoria: 'comunicacao', icon: FileText, descricao: 'Elabore pareceres descritivos', prompt: 'parecer_descritivo' },
  { id: 'conselho_classe', nome: 'Conselho de Classe', categoria: 'comunicacao', icon: GraduationCap, descricao: 'Prepare relatórios para conselho', prompt: 'conselho_classe' },
  { id: 'email_formal', nome: 'E-mail Formal', categoria: 'comunicacao', icon: Send, descricao: 'Escreva e-mails profissionais', prompt: 'email_formal' },
];

// Prompts do sistema para cada ferramenta
const SYSTEM_PROMPTS = {
  plano_aula: `Você é um especialista em educação. Crie um plano de aula completo e detalhado com:
- Tema/Conteúdo
- Objetivos de aprendizagem (alinhados à BNCC quando aplicável)
- Habilidades desenvolvidas
- Metodologia e estratégias
- Recursos necessários
- Desenvolvimento da aula (introdução, desenvolvimento, conclusão)
- Avaliação
- Referências
Seja criativo e use metodologias ativas.`,

  sequencia_didatica: `Você é um especialista em pedagogia. Crie uma sequência didática estruturada com:
- Tema central
- Público-alvo e duração
- Objetivos gerais e específicos
- Conteúdos trabalhados
- Etapas detalhadas (com atividades para cada aula)
- Avaliação processual
- Recursos e materiais`,

  projeto_pedagogico: `Você é um coordenador pedagógico experiente. Elabore um projeto pedagógico com:
- Título e justificativa
- Público-alvo
- Objetivos
- Fundamentação teórica
- Metodologia
- Cronograma
- Recursos necessários
- Avaliação do projeto
- Referências bibliográficas`,

  lista_exercicios: `Você é um professor experiente. Crie uma lista de exercícios com:
- Exercícios variados (múltipla escolha, dissertativos, práticos)
- Diferentes níveis de dificuldade (fácil, médio, difícil)
- Gabarito comentado
- Dicas para os alunos
Organize de forma clara e progressiva.`,

  jogos_pedagogicos: `Você é um especialista em gamificação educacional. Crie jogos e dinâmicas pedagógicas com:
- Nome do jogo
- Objetivo pedagógico
- Materiais necessários
- Regras claras
- Variações possíveis
- Como avaliar a aprendizagem
Seja criativo e engajador!`,

  palavra_cruzada: `Você é um criador de jogos educativos. Crie uma palavra cruzada com:
- Grade/diagrama da palavra cruzada (use caracteres ASCII)
- Lista de palavras horizontais e verticais
- Dicas para cada palavra
- Gabarito
O tema deve estar relacionado ao conteúdo solicitado.`,

  flashcards: `Você é um especialista em técnicas de memorização. Crie flashcards com:
- Frente: pergunta ou termo
- Verso: resposta ou definição
- Dica de memorização
Crie pelo menos 10 flashcards variados. Formate em lista clara.`,

  tarefa_adaptada: `Você é um especialista em educação inclusiva. Adapte a atividade considerando:
- Diferentes níveis de complexidade
- Recursos visuais e concretos
- Instruções claras e objetivas
- Tempo estendido se necessário
- Estratégias de apoio
- Avaliação diferenciada`,

  prova: `Você é um professor experiente. Elabore uma prova completa com:
- Cabeçalho institucional
- Instruções gerais
- Questões variadas (objetivas e dissertativas)
- Distribuição de pontos
- Gabarito
- Critérios de correção`,

  simulado_saeb: `Você é um especialista em avaliações externas. Crie questões no padrão SAEB com:
- Texto-base ou suporte quando necessário
- 4 alternativas (A, B, C, D)
- Apenas uma alternativa correta
- Distratores plausíveis
- Habilidade avaliada
- Gabarito comentado`,

  corretor_redacao: `Você é um corretor de redações experiente. Analise a redação fornecida e forneça:
- Nota por competência (1 a 5):
  * Competência 1: Domínio da norma culta
  * Competência 2: Compreensão do tema
  * Competência 3: Argumentação
  * Competência 4: Coesão
  * Competência 5: Proposta de intervenção
- Nota final
- Pontos positivos
- Pontos a melhorar
- Sugestões específicas
- Comentários linha a linha (principais)`,

  rubrica: `Você é um especialista em avaliação educacional. Crie uma rubrica de avaliação com:
- Critérios de avaliação
- Níveis de desempenho (Insuficiente, Regular, Bom, Excelente)
- Descritores para cada nível
- Pontuação
- Formato de tabela`,

  mapa_mental: `Você é um especialista em organização visual do conhecimento. Crie um mapa mental em texto com:
- Conceito central
- Ramificações principais (use → para indicar)
- Sub-ramificações (use -- para indicar)
- Conexões entre conceitos
- Palavras-chave destacadas
Use hierarquia visual clara com indentação.`,

  resumo: `Você é um especialista em síntese de conteúdos. Crie um resumo com:
- Pontos principais
- Conceitos-chave destacados
- Exemplos relevantes
- Conclusão
Seja claro, objetivo e didático.`,

  reescritor: `Você é um especialista em adaptação de textos. Reescreva o texto considerando:
- Nível de leitura do público-alvo
- Vocabulário adequado
- Estrutura clara
- Manutenção do sentido original
- Inclusão de explicações quando necessário`,

  questoes_video: `Você é um professor que utiliza recursos audiovisuais. Crie questões sobre o vídeo com:
- Questões de compreensão
- Questões de análise crítica
- Questões de conexão com outros conteúdos
- Atividade de produção (escrita ou oral)
- Discussão em grupo`,

  vocabulario: `Você é um professor de línguas. Crie uma lista de vocabulário com:
- Palavra/termo
- Definição clara
- Exemplo de uso em frase
- Sinônimos (quando aplicável)
- Dica de memorização
Organize por categorias ou ordem alfabética.`,

  proposta_redacao: `Você é um professor de redação. Crie uma proposta de redação com:
- Tema motivador
- Textos motivadores (2-3 textos de apoio)
- Instruções claras
- Tipo textual esperado
- Critérios de avaliação
- Número mínimo de linhas`,

  // Novas ferramentas
  pei: `Você é um especialista em Educação Especial. Elabore um Plano Educacional Individualizado (PEI) com:
- Identificação do aluno e diagnóstico
- Características e necessidades específicas
- Habilidades atuais e potencialidades
- Objetivos de curto, médio e longo prazo
- Estratégias e adaptações curriculares
- Recursos e materiais necessários
- Profissionais envolvidos
- Cronograma de avaliação
- Metas mensuráveis`,

  caca_palavras: `Você é um criador de jogos educativos. Crie um caça-palavras com:
- Grade 15x15 (use letras aleatórias para preencher)
- Lista de 10-15 palavras relacionadas ao tema
- As palavras podem estar na horizontal, vertical ou diagonal
- Dicas para cada palavra
- Gabarito com localização das palavras
Formate a grade usando letras separadas por espaço.`,

  relatorio_aluno: `Você é um professor experiente. Elabore um relatório individual do aluno com:
- Aspectos cognitivos (aprendizagem, raciocínio, memória)
- Aspectos socioemocionais (comportamento, relacionamento, participação)
- Aspectos psicomotores (coordenação, organização)
- Pontos fortes observados
- Pontos a desenvolver
- Estratégias recomendadas para casa
- Parecer final
Use linguagem positiva e construtiva.`,

  explicador: `Você é um professor didático e paciente. Explique o conteúdo de forma:
- Clara e objetiva
- Com linguagem adequada ao nível do aluno
- Usando analogias e exemplos do cotidiano
- Dividindo em etapas progressivas
- Incluindo dicas de memorização
- Com perguntas de verificação ao final`,

  comunicado_pais: `Você é um profissional de comunicação escolar. Crie um comunicado aos pais/responsáveis com:
- Cabeçalho institucional
- Saudação apropriada
- Assunto claro e objetivo
- Informações detalhadas
- Instruções ou solicitações (se houver)
- Data e prazos importantes
- Encerramento cordial
- Espaço para assinatura se necessário
Use tom profissional e acolhedor.`,

  parecer_descritivo: `Você é um pedagogo experiente. Elabore um parecer descritivo do aluno contemplando:
- Desenvolvimento cognitivo
- Linguagem oral e escrita
- Raciocínio lógico-matemático
- Aspectos socioafetivos
- Participação e engajamento
- Autonomia e responsabilidade
- Conquistas do período
- Objetivos para o próximo período
Use linguagem técnica mas acessível aos responsáveis.`,

  conselho_classe: `Você é um coordenador pedagógico. Prepare um relatório para Conselho de Classe com:
- Panorama geral da turma
- Desempenho por área de conhecimento
- Alunos com necessidades específicas de acompanhamento
- Estratégias que funcionaram
- Desafios identificados
- Propostas de intervenção
- Encaminhamentos necessários`,

  email_formal: `Você é um profissional de comunicação corporativa. Escreva um e-mail formal com:
- Assunto claro e objetivo
- Saudação adequada ao destinatário
- Introdução contextualizando o assunto
- Desenvolvimento com informações necessárias
- Conclusão com próximos passos ou agradecimento
- Despedida profissional
- Assinatura
Mantenha tom profissional e cordial.`,
};

const FerramentasIA = () => {
  const [categoriaAtiva, setCategoriaAtiva] = useState('todas');
  const [ferramentaSelecionada, setFerramentaSelecionada] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState('');
  
  // Inputs do formulário
  const [formData, setFormData] = useState({
    disciplina: '',
    serie: '',
    tema: '',
    detalhes: '',
    texto_corrigir: '', // Para corretor de redação
  });

  const ferramentasFiltradas = categoriaAtiva === 'todas' 
    ? FERRAMENTAS 
    : FERRAMENTAS.filter(f => f.categoria === categoriaAtiva);

  const abrirFerramenta = (ferramenta) => {
    setFerramentaSelecionada(ferramenta);
    setFormData({
      disciplina: '',
      serie: '',
      tema: '',
      detalhes: '',
      texto_corrigir: '',
    });
    setResultado('');
    setShowModal(true);
  };

  const gerarConteudo = async () => {
    if (!formData.tema && !formData.texto_corrigir) {
      toast.error('Preencha o tema ou conteúdo');
      return;
    }

    setLoading(true);
    setResultado('');

    try {
      const systemPrompt = SYSTEM_PROMPTS[ferramentaSelecionada.prompt];
      
      let userPrompt = '';
      if (ferramentaSelecionada.id === 'corretor_redacao') {
        userPrompt = `Corrija a seguinte redação:\n\n${formData.texto_corrigir}`;
      } else {
        userPrompt = `
Disciplina: ${formData.disciplina || 'Não especificada'}
Série/Ano: ${formData.serie || 'Não especificada'}
Tema/Conteúdo: ${formData.tema}
${formData.detalhes ? `Detalhes adicionais: ${formData.detalhes}` : ''}

Por favor, gere o conteúdo solicitado de forma completa e profissional.
        `.trim();
      }

      const response = await api.post('/ai/generate', {
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        max_tokens: 4000,
      });

      setResultado(response.data.content || response.data.text || response.data);
      toast.success('Conteúdo gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar conteúdo:', error);
      toast.error('Erro ao gerar conteúdo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copiarResultado = () => {
    navigator.clipboard.writeText(resultado);
    toast.success('Copiado para a área de transferência!');
  };

  const downloadResultado = () => {
    const blob = new Blob([resultado], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ferramentaSelecionada?.nome || 'conteudo'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Download iniciado!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wand2 className="w-7 h-7 text-purple-600" />
            Assistente IA do Professor
          </h2>
          <p className="text-muted-foreground mt-1">
            Ferramentas de IA para auxiliar em todas as atividades pedagógicas
          </p>
        </div>
      </div>

      {/* Filtro de Categorias */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={categoriaAtiva === 'todas' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoriaAtiva('todas')}
          className="gap-2"
        >
          <Sparkles className="w-4 h-4" />
          Todas
        </Button>
        {CATEGORIAS.map(cat => {
          const Icon = cat.icon;
          return (
            <Button
              key={cat.id}
              variant={categoriaAtiva === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoriaAtiva(cat.id)}
              className="gap-2"
            >
              <Icon className="w-4 h-4" />
              {cat.nome}
            </Button>
          );
        })}
      </div>

      {/* Grid de Ferramentas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {ferramentasFiltradas.map(ferramenta => {
          const Icon = ferramenta.icon;
          const categoria = CATEGORIAS.find(c => c.id === ferramenta.categoria);
          return (
            <Card 
              key={ferramenta.id}
              className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-purple-300"
              onClick={() => abrirFerramenta(ferramenta)}
            >
              <CardContent className="p-4 flex flex-col items-center text-center">
                <div className={`w-12 h-12 rounded-xl ${categoria?.cor || 'bg-gray-500'} flex items-center justify-center mb-3`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-sm">{ferramenta.nome}</h3>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {ferramenta.descricao}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Modal da Ferramenta */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {ferramentaSelecionada && (
                <>
                  <ferramentaSelecionada.icon className="w-5 h-5 text-purple-600" />
                  {ferramentaSelecionada.nome}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {ferramentaSelecionada?.descricao}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Formulário */}
            {!resultado && (
              <div className="space-y-4">
                {ferramentaSelecionada?.id === 'corretor_redacao' ? (
                  <div className="space-y-2">
                    <Label>Cole a redação para correção *</Label>
                    <Textarea
                      value={formData.texto_corrigir}
                      onChange={(e) => setFormData({...formData, texto_corrigir: e.target.value})}
                      placeholder="Cole aqui o texto da redação que deseja corrigir..."
                      className="min-h-[300px]"
                    />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Disciplina</Label>
                        <Select value={formData.disciplina} onValueChange={(v) => setFormData({...formData, disciplina: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="portugues">Língua Portuguesa</SelectItem>
                            <SelectItem value="matematica">Matemática</SelectItem>
                            <SelectItem value="ciencias">Ciências</SelectItem>
                            <SelectItem value="historia">História</SelectItem>
                            <SelectItem value="geografia">Geografia</SelectItem>
                            <SelectItem value="ingles">Inglês</SelectItem>
                            <SelectItem value="artes">Artes</SelectItem>
                            <SelectItem value="ed_fisica">Educação Física</SelectItem>
                            <SelectItem value="outra">Outra</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Série/Ano</Label>
                        <Select value={formData.serie} onValueChange={(v) => setFormData({...formData, serie: v})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1ano_ef">1º Ano - E.F.</SelectItem>
                            <SelectItem value="2ano_ef">2º Ano - E.F.</SelectItem>
                            <SelectItem value="3ano_ef">3º Ano - E.F.</SelectItem>
                            <SelectItem value="4ano_ef">4º Ano - E.F.</SelectItem>
                            <SelectItem value="5ano_ef">5º Ano - E.F.</SelectItem>
                            <SelectItem value="6ano_ef">6º Ano - E.F.</SelectItem>
                            <SelectItem value="7ano_ef">7º Ano - E.F.</SelectItem>
                            <SelectItem value="8ano_ef">8º Ano - E.F.</SelectItem>
                            <SelectItem value="9ano_ef">9º Ano - E.F.</SelectItem>
                            <SelectItem value="1ano_em">1º Ano - E.M.</SelectItem>
                            <SelectItem value="2ano_em">2º Ano - E.M.</SelectItem>
                            <SelectItem value="3ano_em">3º Ano - E.M.</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Tema/Conteúdo *</Label>
                      <Input
                        value={formData.tema}
                        onChange={(e) => setFormData({...formData, tema: e.target.value})}
                        placeholder="Ex: Frações, Segunda Guerra Mundial, Verbos no presente..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Detalhes adicionais (opcional)</Label>
                      <Textarea
                        value={formData.detalhes}
                        onChange={(e) => setFormData({...formData, detalhes: e.target.value})}
                        placeholder="Adicione informações extras, como número de questões, nível de dificuldade, metodologia preferida..."
                        className="min-h-[100px]"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Resultado */}
            {resultado && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-green-600 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Conteúdo Gerado
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={copiarResultado}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadResultado}>
                      <Download className="w-4 h-4 mr-1" />
                      Salvar TXT
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(`
                        <html>
                        <head>
                          <title>${ferramentaSelecionada?.nome || 'Conteúdo'}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
                            h1 { color: #7B3F98; border-bottom: 2px solid #7B3F98; padding-bottom: 10px; }
                            pre { white-space: pre-wrap; font-family: inherit; }
                          </style>
                        </head>
                        <body>
                          <h1>${ferramentaSelecionada?.nome || 'Conteúdo Gerado'}</h1>
                          <pre>${resultado}</pre>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }}>
                      <FileText className="w-4 h-4 mr-1" />
                      Imprimir
                    </Button>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const blob = new Blob([resultado], { type: 'application/msword' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${ferramentaSelecionada?.nome || 'conteudo'}.doc`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Arquivo Word baixado!');
                      } catch (e) {
                        toast.error('Erro ao gerar Word');
                      }
                    }}>
                      <Download className="w-4 h-4 mr-1" />
                      Salvar DOC
                    </Button>
                    <Button variant="default" size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                      const email = prompt('Digite o e-mail para enviar:');
                      if (email) {
                        try {
                          await api.post('/ai/send-content-email', {
                            to_email: email,
                            subject: ferramentaSelecionada?.nome || 'Conteúdo Gerado',
                            content: resultado,
                          });
                          toast.success('E-mail enviado com sucesso!');
                        } catch (e) {
                          toast.error('Erro ao enviar e-mail');
                        }
                      }
                    }}>
                      <Send className="w-4 h-4 mr-1" />
                      Enviar Email
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-50 border rounded-lg p-4 whitespace-pre-wrap text-sm max-h-[400px] overflow-y-auto">
                  {resultado}
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setResultado('')}
                  className="w-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Gerar novo conteúdo
                </Button>
              </div>
            )}
          </div>

          <DialogFooter>
            {!resultado && (
              <>
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button onClick={gerarConteudo} disabled={loading} className="gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Gerar com IA
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FerramentasIA;
